/**
 * UGC Gallery Toggle.
 *
 * Swaps the PDP gallery between product media and UGC images IN PLACE, working
 * with BOTH gallery engines this theme ships:
 *
 *   - thumbnails layout  -> <product-slider-thumbnails> (Flickity-driven, has a
 *     separate #Product-Thumbnails rail). We move nodes in/out of #Product-Slider
 *     and #Product-Thumbnails, then reuse the element's own public reInit() +
 *     setupProductGallery() to rebuild Flickity over the swapped cells.
 *
 *   - grid / collage / list layouts -> <product-slider> (native CSS scroll-snap,
 *     NO Flickity, NO thumbnail rail). The element runs a MutationObserver on its
 *     childList that re-binds PhotoSwipe whenever children change, so we just move
 *     the slide nodes in/out and call onPaginationResize() to refresh the mobile
 *     pager. No Flickity to rebuild.
 *
 * Zero edits to product.js / product-thumbnails.js.
 *
 * DOM contract (rendered by sections/main-product.liquid, all layouts):
 *   #Product-Slider                                    the slider element (either custom element)
 *   [data-gallery-tabs] with two [data-tab-target="media"|"ugc"] buttons
 *   template[data-ugc-slides]  UGC .product-images__slide nodes (all .is-active)
 *   template[data-ugc-thumbs]  UGC .product-thumbnail nodes (thumbnails layout only)
 */
(function () {
  'use strict';

  class UgcGalleryToggle {
    constructor(root) {
      this.root = root; // .thb-product-detail
      this.slider = root.querySelector('#Product-Slider');
      this.tabs = root.querySelector('[data-gallery-tabs]');
      this.slidesTpl = root.querySelector('template[data-ugc-slides]');
      this.rail = root.querySelector('#Product-Thumbnails'); // thumbnails layout only
      this.thumbsTpl = root.querySelector('template[data-ugc-thumbs]'); // thumbnails layout only

      if (!this.slider || !this.tabs || !this.slidesTpl) {
        return;
      }

      // Flickity layout has a custom element with reInit(); native layout does not.
      this.isFlickity = this.slider.tagName.toLowerCase() === 'product-slider-thumbnails';

      this.mode = 'media'; // 'media' | 'ugc'
      this.ugcMounted = false;
      this._mediaSelectedId = null;

      // Detached holders for whichever set is NOT currently in the DOM.
      this.mediaSlidesHolder = document.createElement('div');
      this.ugcSlidesHolder = document.createElement('div');
      this.mediaThumbsHolder = document.createElement('div');
      this.ugcThumbsHolder = document.createElement('div');

      this.bindTabs();
      this.bindVariantGuard();
    }

    /* ---------------- tab UI ---------------- */
    bindTabs() {
      this.tabs.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-tab-target]');
        if (!btn) return;
        const target = btn.dataset.tabTarget;
        if (target === this.mode) return; // active tab is hidden anyway
        this.switchTo(target);
      });
    }

    setActiveTab(mode) {
      this.tabs.querySelectorAll('[data-tab-target]').forEach((b) => {
        const isActive = b.dataset.tabTarget === mode; // active = current view; CSS hides it
        b.classList.toggle('is-active', isActive);
        b.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });
    }

    /* ---------------- core swap ---------------- */
    switchTo(mode) {
      if (mode === this.mode) return;

      // Flickity must exist before we can reInit. Defer if the element has not
      // upgraded / inited yet (click landed before connectedCallback's init()).
      if (this.isFlickity && !this.slider.flkty) {
        requestAnimationFrame(() => this.switchTo(mode));
        return;
      }

      if (this.mode === 'media') this.rememberMediaSelection();

      if (mode === 'ugc') this.mountUgc();
      else this.mountMedia();

      this.mode = mode;
      this.setActiveTab(mode);

      if (this.isFlickity) {
        // Rebuild Flickity over the new .is-active cells + rebind thumb clicks.
        this.slider.reInit(this.slider.imageSetIndex);
        // reInit() does NOT call setupProductGallery(), so PhotoSwipe zoom links
        // would stay bound to the detached set. Rebind them over the live set.
        this.slider.setupProductGallery();
        // adaptiveHeight read at reInit can be stale across aspect-ratio changes.
        requestAnimationFrame(() => {
          if (this.slider.flkty) this.slider.flkty.resize();
        });
      } else {
        // Native <product-slider>: its MutationObserver re-binds PhotoSwipe on the
        // childList change we just made. We only refresh the mobile pager geometry.
        this.refreshNativePager();
      }
    }

    refreshNativePager() {
      const run = () => {
        // <product-slider> caches its slide list once in connectedCallback
        // (this.sliderItems = querySelectorAll('[id^="Slide-"]')). After we swap
        // the children that snapshot still points at the now-detached old nodes
        // (clientWidth === 0), so getVisibleSliderItems() returns [] and the pager
        // total shows 0. Refresh the snapshot to the live nodes before recomputing.
        if ('sliderItems' in this.slider) {
          this.slider.sliderItems = this.slider.querySelectorAll('[id^="Slide-"]');
        }
        if (typeof this.slider.onPaginationResize === 'function') {
          this.slider.onPaginationResize();
        } else if (typeof this.slider.updatePagination === 'function') {
          this.slider.updatePagination();
        }
        // Reset horizontal scroll so the pager starts at slide 1.
        this.slider.scrollTo({ left: 0, behavior: 'auto' });
        // Fallback: onPaginationResize() early-returns when fewer than 2 slides
        // are visible, leaving the total stale. Sync the counter directly from
        // the live DOM (active slides) so it never shows 0 or a stale count.
        this.syncPagerCounter();
      };
      requestAnimationFrame(run);
    }

    syncPagerCounter() {
      const pager = this.slider.parentElement
        ? this.slider.parentElement.querySelector('.product-images-buttons')
        : null;
      if (!pager) return;
      const totalEl = pager.querySelector('.slider-counter--total');
      const currentEl = pager.querySelector('.slider-counter--current');
      // Count slides the slider actually renders: any is-active, else all.
      let slides = this.slider.querySelectorAll('.product-images__slide.is-active');
      if (!slides.length) slides = this.slider.querySelectorAll('.product-images__slide');
      if (totalEl) totalEl.textContent = slides.length;
      if (currentEl) currentEl.textContent = '1';
    }

    rememberMediaSelection() {
      const sel =
        this.slider.querySelector('.product-images__slide.is-initial-selected') ||
        this.slider.querySelector('.product-images__slide.is-active');
      this._mediaSelectedId = sel ? sel.id : null;
    }

    mountUgc() {
      // Move current (media) nodes out to their holders.
      this.moveChildren(this.slider, this.mediaSlidesHolder);
      if (this.rail) this.moveChildren(this.rail, this.mediaThumbsHolder);

      // Bring UGC in: first time from <template>, afterwards from the holder.
      if (!this.ugcMounted) {
        this.slider.appendChild(this.slidesTpl.content.cloneNode(true));
        if (this.rail && this.thumbsTpl) {
          this.rail.appendChild(this.thumbsTpl.content.cloneNode(true));
        }
        this.ugcMounted = true;
      } else {
        this.moveChildren(this.ugcSlidesHolder, this.slider);
        if (this.rail) this.moveChildren(this.ugcThumbsHolder, this.rail);
      }

      // UGC nodes already carry is-active (+ is-initial-selected on first) from
      // Liquid. Normalize defensively in case of a prior partial state.
      this.normalize(this.slider, '.product-images__slide');
      if (this.rail) this.normalize(this.rail, '.product-thumbnail');
    }

    mountMedia() {
      // Move UGC out (keep nodes for next toggle), bring media back.
      this.moveChildren(this.slider, this.ugcSlidesHolder);
      if (this.rail) this.moveChildren(this.rail, this.ugcThumbsHolder);
      this.moveChildren(this.mediaSlidesHolder, this.slider);
      if (this.rail) this.moveChildren(this.mediaThumbsHolder, this.rail);

      // Restore the media slide/thumb the user was last on (if known).
      if (this._mediaSelectedId) {
        const slide = this.byId(this.slider, this._mediaSelectedId);
        if (slide) {
          this.slider
            .querySelectorAll('.product-images__slide.is-initial-selected')
            .forEach((s) => s.classList.remove('is-initial-selected'));
          slide.classList.add('is-active', 'is-initial-selected');

          if (this.rail) {
            const thumbId = this._mediaSelectedId.replace('Slide-', 'Thumb-');
            const thumb = this.byId(this.rail, thumbId);
            if (thumb) {
              this.rail
                .querySelectorAll('.product-thumbnail.is-initial-selected')
                .forEach((t) => t.classList.remove('is-initial-selected'));
              thumb.classList.add('is-active', 'is-initial-selected');
            }
          }
        }
      }
    }

    /* ---------------- variant guard ----------------
       Both gallery engines update the main image on variant change by querying
       #Slide-{section}-{variantMediaId} and adding .is-active with NO null guard
       (product.js setActiveMedia:215 / setActiveMediaSlider:306). In UGC mode that
       slide is detached, so the query returns null and the variant change throws.
       A capture-phase 'change' listener runs BEFORE product.js's bubble-phase
       handler (product.js:14), letting us revert to media DOM first. We do NOT
       reInit / repaginate here — product.js owns the rebuild that follows. */
    bindVariantGuard() {
      this.root.querySelectorAll('variant-selects, variant-radios').forEach((vs) => {
        vs.addEventListener(
          'change',
          () => {
            if (this.mode === 'ugc') this.revertToMediaSilently();
          },
          true // capture
        );
      });
    }

    revertToMediaSilently() {
      this.moveChildren(this.slider, this.ugcSlidesHolder);
      if (this.rail) this.moveChildren(this.rail, this.ugcThumbsHolder);
      this.moveChildren(this.mediaSlidesHolder, this.slider);
      if (this.rail) this.moveChildren(this.mediaThumbsHolder, this.rail);
      this.mode = 'media';
      this.setActiveTab('media');
      // No reInit / repaginate here — product.js owns the rebuild now.
    }

    /* ---------------- helpers ---------------- */
    moveChildren(from, to) {
      while (from.firstChild) to.appendChild(from.firstChild);
    }

    normalize(container, selector) {
      const items = container.querySelectorAll(selector);
      items.forEach((el, i) => {
        el.classList.add('is-active');
        el.classList.toggle('is-initial-selected', i === 0);
      });
    }

    byId(scope, id) {
      if (window.CSS && CSS.escape) return scope.querySelector('#' + CSS.escape(id));
      return scope.querySelector('[id="' + id.replace(/"/g, '\\"') + '"]');
    }
  }

  function init() {
    document.querySelectorAll('.thb-product-detail').forEach((root) => {
      if (root.dataset.ugcToggleBound) return;
      if (!root.querySelector('[data-gallery-tabs]')) return; // no UGC for this product
      root.dataset.ugcToggleBound = 'true';
      new UgcGalleryToggle(root);
    });
  }

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);
})();
